<script lang="ts" setup>
import CSpin from '@lib/components/CSpin/CSpin.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import CSecondaryNav from '@lib/components/CSecondaryNav/CSecondaryNav.vue'
import CCanvasSelectorListItem from './CCanvasSelectorListItem.vue'
import type { CanvasRetrieve } from '@openapi'
import { useI18n } from 'vue-i18n'
import { CanvasesViewsTypes } from '@lib/types/canvases'
import { computed } from 'vue'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    search?: string
    activeView?: string
    canvases?: CanvasRetrieve[]
    defaultTemplate?: CanvasRetrieve
    selectedCanvas?: any
    isLoading?: boolean
    showTemplates?: boolean
  }>(),
  {
    search: '',
    activeView: CanvasesViewsTypes.SAVED,
    showTemplates: true,
  },
)

const defaultCanvas = computed(() => {
  return props.defaultTemplate
    ? { ...props.defaultTemplate, subtitle: t('canvasUI.CCanvasSelector.defaultSubtitle') }
    : null
})

const emit = defineEmits(['update:search', 'update:activeView', 'update:selectedCanvas'])

interface OpenParams {
  name: string
}

const onViewSelect = ({ name }: OpenParams) => {
  emit('update:activeView', name)
}

const onCanvasSelect = (canvas: any) => {
  emit('update:selectedCanvas', props.selectedCanvas?.id !== canvas.id ? canvas : null)
}
</script>

<template>
  <div class="c-canvas-selector flex flex-col w-full">
    <CSecondaryNav
      v-if="showTemplates"
      :activeRouteName="activeView"
      :navItems="[
        {
          name: CanvasesViewsTypes.SAVED,
          text: t('canvasUI.CCanvasSelector.nav.saved'),
        },
        {
          name: CanvasesViewsTypes.TEMPLATES,
          text: t('canvasUI.CCanvasSelector.nav.templates'),
        },
      ]"
      @open="onViewSelect"
    />
    <div class="my-4">
      <CSearch :model-value="search" @update:model-value="emit('update:search', $event)" />
    </div>
    <CCanvasSelectorListItem
      v-if="defaultCanvas"
      :active-canvas-id="selectedCanvas?.id"
      :canvas="defaultCanvas"
      class="mb-3"
      @select-canvas="onCanvasSelect"
    />
    <div class="flex flex-col h-full overflow-auto">
      <div v-if="canvases?.length" class="c-canvas-selector__list-title mb-1">
        {{ t('canvasUI.CCanvasSelector.lastUsed') }}
      </div>
      <CSpin class="h-full" :show="isLoading">
        <div v-if="canvases?.length" class="flex flex-col gap-2">
          <CCanvasSelectorListItem
            v-for="canvas in canvases"
            :key="canvas.id"
            :active-canvas-id="selectedCanvas?.id"
            :canvas="canvas"
            @select-canvas="onCanvasSelect"
          />
        </div>
        <div v-else-if="!isLoading" class="flex flex-col flex-1 items-center justify-center">
          <CIcon class="mb-2" fa-type="fal" icon="memo-circle-info" size="24px" />
          <span class="text-size-xs">{{ t('canvasUI.CCanvasSelector.noCanvases') }}</span>
        </div>
      </CSpin>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-canvas-selector__list-title {
  font-size: 0.75rem;
  line-height: 1rem;
}

:deep(.c-secondary-nav__nav-items) {
  width: 100%;
}

:deep(.c-secondary-nav__button) {
  flex: 1;
}

:deep(.n-spin-content) {
  display: flex;
  height: 100%;
  flex-direction: column;
}
</style>
